import { useEffect } from 'react'
import { markExperimentExposed } from '@/core/experiments'
import {
  useLifecycleExperiment,
  usePostDecisionUser,
} from '@/core/feature-flags'
import { activePlan, isReturnFlowType } from '../gate'

// Both arms report the same thing: this user opened this plan. Mount it only
// where the takeover can fire — the server burns the treatment arm's one
// impression here, so a report from elsewhere costs a greeting on iOS or
// Android that the user would otherwise have received.
//
// `enabled` is that same rule applied in time rather than in place: a plan surface
// about to be covered by a payment-method screen has not been opened yet. iOS gets
// this by construction — a staged notice keeps MainTabCoordinator from being built,
// and the report lives inside it — so passing false while the verdict is unknown or
// showing is what makes the two clients count the same openings.
export function useExposureReport(enabled = true): void {
  const user = usePostDecisionUser()
  const userId = user?.id
  const isStoic = user?.isStoic
  const plan = activePlan(user)
  const planId = plan?.id
  // The verdict alone, so both arms report the same way: the treatment reports
  // that it was served, not that it rendered.
  const arm = isReturnFlowType(plan?.startFlow?.type) ? 'return' : 'control'
  const experiment = useLifecycleExperiment('planOpened')
  const experimentName = experiment?.name
  const anchorPlanId = experiment?.planId

  useEffect(() => {
    if (!enabled) return
    if (!userId || !planId || !experimentName) return
    // Neither arm: no web surface takes a stoic user to /return, so enrolling
    // them here spends the impression iOS and Android are waiting to deliver.
    if (isStoic) return
    // The only other gate. A check only the treatment arm can fail — did the
    // flow render? — filters that arm on device health, which correlates with
    // the retention being measured.
    if (anchorPlanId && anchorPlanId !== planId) return
    markExperimentExposed({ userId, planId, experimentName, arm })
  }, [enabled, userId, isStoic, planId, arm, experimentName, anchorPlanId])
}
